import type { Permission, Role } from '@bekebi/domain';

/**
 * The Admin view's sections. One definition drives both the sidebar and the guarded routes, so a
 * section is shown only to users whose session lists its permission — and the same permission
 * guards its route. Permission names are the backend's own (packages/domain); the API still checks
 * every request itself.
 */
export interface NavItem {
  /** Route segment under /app. */
  path: string;
  label: string;
  permission: Permission;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const ADMIN_NAV: NavSection[] = [
  {
    label: 'Overview',
    items: [{ path: 'dashboard', label: 'Dashboard', permission: 'services.view' }],
  },
  {
    label: 'Services',
    items: [
      { path: 'services', label: 'Services', permission: 'services.view' },
      { path: 'monitoring', label: 'Monitoring', permission: 'monitoring.view' },
      { path: 'renewals', label: 'Renewals', permission: 'renewals.view' },
      { path: 'mailboxes', label: 'Mailboxes', permission: 'mail.mailboxes.view' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { path: 'infrastructure', label: 'Infrastructure', permission: 'infrastructure.view' },
      { path: 'payment-account', label: 'Payment Account', permission: 'payment_account.view' },
      { path: 'users', label: 'Users', permission: 'users.manage' },
      { path: 'activity', label: 'Activity', permission: 'audit.view' },
    ],
  },
];

export const ADMIN_NAV_ITEMS: NavItem[] = ADMIN_NAV.flatMap((section) => section.items);

/**
 * The ICT view (internal role `operations`): the same portal and routes, a smaller navigation.
 * Admin-only areas (Infrastructure, Monitoring, Users, Activity) are not listed — and their routes
 * refuse ICT, because the session lacks their permissions; the API refuses them too.
 */
export const ICT_NAV: NavSection[] = [
  {
    label: 'Overview',
    items: [{ path: 'dashboard', label: 'Dashboard', permission: 'services.view' }],
  },
  {
    label: 'Services',
    items: [
      { path: 'services', label: 'Services', permission: 'services.view' },
      { path: 'renewals', label: 'Renewals', permission: 'renewals.view' },
      { path: 'payment-account', label: 'Payment Account', permission: 'payment_account.view' },
      { path: 'mailboxes', label: 'Mailboxes', permission: 'mail.mailboxes.view' },
    ],
  },
  {
    label: 'You',
    items: [{ path: 'my-activity', label: 'My Activity', permission: 'audit.view_own' }],
  },
];

export const ICT_NAV_ITEMS: NavItem[] = ICT_NAV.flatMap((section) => section.items);

export function navForRole(role: Role): NavSection[] {
  return role === 'admin' ? ADMIN_NAV : ICT_NAV;
}

/** The sections a permission list allows (empty sections are dropped). */
export function visibleNav(
  permissions: readonly string[],
  sections: NavSection[] = ADMIN_NAV,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => permissions.includes(item.permission)),
    }))
    .filter((section) => section.items.length > 0);
}
